import { useEffect, useState } from 'react';
import type {
  CreateShippingMethodRequest,
  RateBandInput,
  ShippingMethodAdminDto,
  ShippingZoneAdminDto,
  ZoneMemberKind,
} from '@ecom/contracts';
import { useShippingStore } from '@/stores/shipping.store';
import { useAuthStore } from '@/stores/auth.store';
import { useOrdersStore } from '@/stores/orders.store';
import { AsyncSelect } from '@/components/async-select';
import { categoryOptions, cityOptions, countryOptions, regionOptions, stockLocationOptions } from '@/lib/options';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { api, errorMessage, withSession } from '@/lib/api-client';
import { halalasToRiyals, riyalsToHalalas, thousandthsToUnits, unitsToThousandths } from '@/lib/price-input';
import { SETTINGS_PAGES } from '@/lib/settings-pages';
import { CloseIcon, PlusIcon, TrashIcon, TruckIcon } from '@/components/icons';
import { BilingualField, ToggleField } from '@/components/fields';
import { Section, Sheet, Tabs } from '@/components/page';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  IconButton,
  Input,
  Pill,
  Select,
  Skeleton,
  cx,
} from '@/components/ui';
import { SettingsPage } from '@/pages/settings';
import { SchemaSections } from '@/pages/settings/schema';

type ShippingTab = 'zones' | 'options';

/**
 * Shipping zones.
 *
 * The last Phase-1 leftover: checkout has priced deliveries from these tables
 * since the beginning, with no way for anyone in the business to change them.
 *
 * Laid out as zone list → one zone open, the same shape as Settings, because a
 * zone is the unit people think in: "what does Riyadh cost?" is a question
 * about a zone, not about a method or a band. The delivery calendar and the
 * rest of the shipping settings sit on the second tab, beside the zones.
 */
export function ShippingPage() {
  const { zones, loading, error, load } = useShippingStore();
  const user = useAuthStore((state) => state.user);
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [tab, setTab] = useState<ShippingTab>('zones');
  const [selected, setSelected] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    void load();
  }, [load]);

  // The API enforces this; hiding the form is a courtesy, not the boundary.
  if (user?.role !== 'OWNER') {
    return (
      <SettingsPage title="settingsShipping" subtitle="settingsShippingHint">
        <Card className="flex flex-1 flex-col">
          <EmptyState title={t('ownerOnlySettings')} icon={<TruckIcon />} />
        </Card>
      </SettingsPage>
    );
  }

  const active = zones.find((zone) => zone.id === selected) ?? zones[0];

  return (
    <SettingsPage
      title="settingsShipping"
      subtitle="settingsShippingHint"
      actions={
        tab === 'zones' ? (
          <Button onClick={() => setAdding(true)}>
            <PlusIcon className="size-4" />
            {t('addZone')}
          </Button>
        ) : undefined
      }
      tabs={
        <Tabs
          label={t('settingsShipping')}
          value={tab}
          onChange={setTab}
          items={[
            { id: 'zones', label: t('shippingZonesAndRates') },
            { id: 'options', label: t('shippingDeliveryOptions') },
          ]}
        />
      }
    >
      {tab === 'options' ? (
        <SchemaSections keys={SETTINGS_PAGES.shipping} />
      ) : loading && zones.length === 0 ? (
        <Skeleton className="h-96" />
      ) : (
        <>
          {error ? <Alert>{error}</Alert> : null}

          {zones.length === 0 ? (
            <Card className="flex flex-1 flex-col">
              <EmptyState title={t('noShippingZones')} body={t('shippingSubtitle')} icon={<TruckIcon />} />
            </Card>
          ) : (
            <div className="grid items-start gap-5 lg:grid-cols-[14rem_minmax(0,1fr)]">
              <Card className="overflow-hidden lg:sticky lg:top-5">
                <nav aria-label={t('shippingZones')}>
                  <ul className="divide-y divide-line">
                    {zones.map((zone) => (
                      <li key={zone.id}>
                        <button
                          type="button"
                          onClick={() => setSelected(zone.id)}
                          aria-current={zone.id === active?.id}
                          className={cx(
                            'flex w-full items-center gap-2 px-4 py-3 text-start transition-colors',
                            zone.id === active?.id ? 'bg-brand-50' : 'hover:bg-surface-sunken',
                          )}
                        >
                          <span className="min-w-0 flex-1">
                            <span
                              className={cx(
                                'block truncate text-sm',
                                zone.id === active?.id ? 'font-medium text-brand-700' : 'text-ink',
                              )}
                            >
                              {zone.translations?.[locale] || zone.name}
                            </span>
                            <span data-numeric className="block text-[11px] text-ink-faint">
                              {zone.members.length} · {zone.methods.length}
                            </span>
                          </span>
                          {zone.isDefault ? <Pill tone="info">{t('defaultZone')}</Pill> : null}
                          {!zone.isActive ? <Pill tone="neutral">{t('inactive')}</Pill> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </Card>

              <div className="flex min-w-0 flex-col gap-5">
                {active ? <ZoneEditor key={active.id} zone={active} /> : null}
              </div>
            </div>
          )}

          {adding ? <NewZoneSheet onClose={() => setAdding(false)} /> : null}
        </>
      )}
    </SettingsPage>
  );
}

/** A new zone, named in a panel beside the list. */
function NewZoneSheet({ onClose }: { onClose: () => void }) {
  const { createZone, saving, error } = useShippingStore();
  const t = useT();
  const [name, setName] = useState('');
  const [ar, setAr] = useState('');
  // The store's error is shared by the page; only one this panel caused belongs in it.
  const [tried, setTried] = useState(false);

  return (
    <Sheet
      open
      title={t('addZone')}
      subtitle={t('addZoneHint')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            disabled={saving || name.trim() === ''}
            onClick={async () => {
              setTried(true);
              const created = await createZone({
                name: name.trim(),
                translations: { ar: ar.trim() || name.trim(), en: name.trim() },
              });
              if (created) onClose();
            }}
          >
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {tried && error ? <Alert>{error}</Alert> : null}
        <Field label={t('zoneNameInternal')} htmlFor="zone-name">
          <Input id="zone-name" dir="ltr" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('zoneNameArabic')} htmlFor="zone-name-ar">
          <Input id="zone-name-ar" dir="rtl" value={ar} onChange={(e) => setAr(e.target.value)} />
        </Field>
      </div>
    </Sheet>
  );
}

function ZoneEditor({ zone }: { zone: ShippingZoneAdminDto }) {
  const { updateZone, removeMember, saving } = useShippingStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [addingMethod, setAddingMethod] = useState(false);

  return (
    <>
      <Section
        title={zone.translations?.[locale] || zone.name}
        subtitle={zone.isDefault ? t('defaultZoneHint') : undefined}
        action={
          zone.isDefault ? undefined : (
            <>
              <Button
                variant="ghost"
                size="sm"
                disabled={saving}
                onClick={() => void updateZone(zone.id, { isDefault: true })}
              >
                {t('makeDefaultZone')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={saving}
                onClick={() => void updateZone(zone.id, { isActive: !zone.isActive })}
              >
                {zone.isActive ? t('deactivate') : t('activate')}
              </Button>
            </>
          )
        }
      >
        <div>
          <h3 className="mb-1 text-sm font-medium text-ink">{t('zoneLocations')}</h3>
          <p className="mb-3 text-xs text-ink-muted">
            {zone.isDefault ? t('defaultZoneLocationsHint') : t('zoneLocationsHint')}
          </p>

          {zone.members.length === 0 ? (
            <p className="mb-4 text-sm text-ink-faint">{t('noLocations')}</p>
          ) : (
            <ul className="mb-4 flex flex-wrap gap-2">
              {zone.members.map((member) => (
                <li
                  key={member.id}
                  className="flex items-center gap-2 rounded-full border border-line bg-surface-sunken py-1 ps-3 pe-1.5 text-xs text-ink"
                >
                  <span className="text-ink-faint">{t(`memberKind${member.kind}` as 'memberKindCITY')}</span>
                  <span>{member.name}</span>
                  <button
                    type="button"
                    aria-label={t('remove')}
                    title={t('remove')}
                    disabled={saving}
                    onClick={() => void removeMember(member.id)}
                    className="inline-flex size-5 items-center justify-center rounded-full text-ink-faint transition-colors hover:bg-surface hover:text-danger"
                  >
                    <CloseIcon className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <LocationPicker zoneId={zone.id} />
        </div>
      </Section>

      {zone.methods.map((method) => (
        <MethodCard key={method.id} method={method} />
      ))}

      <div>
        <Button variant="secondary" onClick={() => setAddingMethod(true)}>
          <PlusIcon className="size-4" />
          {t('addMethod')}
        </Button>
      </div>

      {addingMethod ? <NewMethodSheet zoneId={zone.id} onClose={() => setAddingMethod(false)} /> : null}
    </>
  );
}

/**
 * Country → region → city, each list searched server-side.
 *
 * Never fetch-all-and-filter: KSA alone has thousands of cities, and the
 * platform's rule is that an API-backed dropdown searches on the server with
 * debounced input.
 */
function LocationPicker({ zoneId }: { zoneId: string }) {
  const { addMember, saving } = useShippingStore();
  const t = useT();

  const [kind, setKind] = useState<ZoneMemberKind>('CITY');
  const [countryId, setCountryId] = useState('');
  const [regionId, setRegionId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Only the default country is fetched up front, and only to save a click in
  // the single-country market this platform ships for. Everything else — every
  // region, every city — is searched through the API as it is typed.
  useEffect(() => {
    void withSession(() => api.geography.countries({ limit: 1 }))
      .then((page) => {
        if (page.items.length > 0) setCountryId((current) => current || page.items[0].id);
      })
      .catch((cause) => setError(errorMessage(cause, 'Could not load countries.')));
  }, []);

  const add = (memberId: string) => {
    if (memberId) void addMember(zoneId, kind, memberId);
  };

  return (
    <div className="flex flex-col gap-3">
      {error ? <Alert>{error}</Alert> : null}

      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('locationKind')} htmlFor={`kind-${zoneId}`}>
          <Select
            id={`kind-${zoneId}`}
            value={kind}
            onChange={(event) => setKind(event.target.value as ZoneMemberKind)}
            className="w-40"
          >
            <option value="CITY">{t('memberKindCITY')}</option>
            <option value="REGION">{t('memberKindREGION')}</option>
            <option value="COUNTRY">{t('memberKindCOUNTRY')}</option>
          </Select>
        </Field>

        <Field label={t('country')} htmlFor={`country-${zoneId}`}>
          <div className="w-48">
            <AsyncSelect
              id={`country-${zoneId}`}
              value={countryId}
              allowClear={false}
              emptyLabel={t('choose')}
              fetchPage={countryOptions.page}
              onChange={(id) => {
                setCountryId(id);
                // A region belongs to one country; keeping the old one selected
                // would offer a location that is not in this country at all.
                setRegionId('');
              }}
            />
          </div>
        </Field>

        {kind === 'COUNTRY' ? (
          <Button variant="secondary" disabled={saving || !countryId} onClick={() => add(countryId)}>
            {t('addLocation')}
          </Button>
        ) : (
          <Field label={t('region')} htmlFor={`region-${zoneId}`}>
            <div className="w-48">
              <AsyncSelect
                id={`region-${zoneId}`}
                value={regionId}
                emptyLabel={t('choose')}
                fetchPage={regionOptions(countryId).page}
                onChange={(id) => setRegionId(id)}
              />
            </div>
          </Field>
        )}

        {kind === 'REGION' ? (
          <Button variant="secondary" disabled={saving || !regionId} onClick={() => add(regionId)}>
            {t('addLocation')}
          </Button>
        ) : null}
      </div>

      {kind === 'CITY' && regionId ? (
        <div className="flex flex-wrap items-end gap-3">
          {/* The search box and the list are one control now: the picker
              searches as it is typed, so a separate field for the term was
              two places to look at the same thing. */}
          <Field label={t('city')} htmlFor={`city-${zoneId}`}>
            <div className="w-56">
              <AsyncSelect
                id={`city-${zoneId}`}
                value=""
                allowClear={false}
                emptyLabel={t('choose')}
                placeholder={t('searchCity')}
                fetchPage={cityOptions(regionId).page}
                onChange={(id) => add(id)}
              />
            </div>
          </Field>
        </div>
      ) : null}
    </div>
  );
}

function MethodCard({ method }: { method: ShippingMethodAdminDto }) {
  const { updateMethod, deactivateMethod, saving } = useShippingStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [bands, setBands] = useState<DraftBand[]>(() => method.bands.map(toDraft));
  const [estimate, setEstimate] = useState(method.deliveryEstimate ?? '');
  const [timing, setTiming] = useState<TimingDraft>(() => toTimingDraft(method));
  const [rules, setRules] = useState<RulesDraft>(() => toRulesDraft(method));
  const rulesOut = rulesBody(rules);

  return (
    <Section
      title={method.translations?.[locale] || method.name}
      subtitle={method.isActive ? undefined : t('methodInactive')}
      action={
        <Button
          variant="secondary"
          size="sm"
          disabled={saving}
          onClick={() =>
            method.isActive
              ? void deactivateMethod(method.id)
              : void updateMethod(method.id, { isActive: true })
          }
        >
          {method.isActive ? t('deactivate') : t('activate')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="max-w-sm">
          <Field label={t('deliveryEstimate')} htmlFor={`estimate-${method.id}`} hint={t('deliveryEstimateHint')}>
            <Input
              id={`estimate-${method.id}`}
              value={estimate}
              onChange={(event) => setEstimate(event.target.value)}
            />
          </Field>
        </div>

        <TimingFields idPrefix={method.id} draft={timing} onChange={setTiming} />

        <BandEditor bands={bands} onChange={setBands} />

        <RulesFields idPrefix={method.id} draft={rules} onChange={setRules} />

        <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
          <Button
            disabled={saving || rulesOut === null}
            onClick={() => {
              const parsed = parseBands(bands);
              if (!parsed || !rulesOut) return;
              void updateMethod(method.id, { bands: parsed, deliveryEstimate: estimate, ...timingBody(timing), ...rulesOut });
            }}
          >
            {saving ? t('saving') : t('save')}
          </Button>
          <span className="text-xs text-ink-muted">{t('bandsHint')}</span>
        </div>
      </div>
    </Section>
  );
}

/** A new delivery method for one zone, written in a panel beside it. */
function NewMethodSheet({ zoneId, onClose }: { zoneId: string; onClose: () => void }) {
  const { createMethod, saving, error } = useShippingStore();
  const t = useT();

  const [name, setName] = useState('');
  const [ar, setAr] = useState('');
  const [estimate, setEstimate] = useState('');
  const [bands, setBands] = useState<DraftBand[]>([{ from: '0.00', to: '', price: '0.00' }]);
  const [timing, setTiming] = useState<TimingDraft>(() => toTimingDraft());
  const [rules, setRules] = useState<RulesDraft>(() => toRulesDraft());
  const rulesOut = rulesBody(rules);
  // The store's error is shared by the page; only one this panel caused belongs in it.
  const [tried, setTried] = useState(false);

  return (
    <Sheet
      open
      size="lg"
      title={t('addMethod')}
      subtitle={t('addMethodHint')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            disabled={saving || name.trim() === '' || rulesOut === null}
            onClick={async () => {
              const parsed = parseBands(bands);
              if (!parsed || !rulesOut) return;

              setTried(true);
              const created = await createMethod(zoneId, {
                name: name.trim(),
                translations: { ar: ar.trim() || name.trim(), en: name.trim() },
                deliveryEstimate: estimate || undefined,
                bands: parsed,
                ...timingBody(timing),
                ...rulesOut,
              });
              if (created) onClose();
            }}
          >
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {tried && error ? <Alert>{error}</Alert> : null}

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('methodNameInternal')} htmlFor="method-name">
            <Input id="method-name" dir="ltr" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('methodNameArabic')} htmlFor="method-name-ar">
            <Input id="method-name-ar" dir="rtl" value={ar} onChange={(e) => setAr(e.target.value)} />
          </Field>
          <Field label={t('deliveryEstimate')} htmlFor="method-estimate">
            <Input id="method-estimate" value={estimate} onChange={(e) => setEstimate(e.target.value)} />
          </Field>
        </div>

        <TimingFields idPrefix="new" draft={timing} onChange={setTiming} />

        <BandEditor bands={bands} onChange={setBands} />

        <RulesFields idPrefix="new" draft={rules} onChange={setRules} />
      </div>
    </Sheet>
  );
}

/** The timing and pickup inputs as typed — strings until saved. */
interface TimingDraft {
  min: string;
  max: string;
  cutoff: string;
  isPickup: boolean;
  instructions: { ar: string; en: string };
  /** The branch it is collected from (ADR-089); empty for none named. */
  locationId: string;
}

function toTimingDraft(method?: ShippingMethodAdminDto): TimingDraft {
  return {
    min: method?.minBusinessDays?.toString() ?? '',
    max: method?.maxBusinessDays?.toString() ?? '',
    cutoff: method?.cutoffTime ?? '',
    isPickup: method?.isPickup ?? false,
    instructions: method?.pickupInstructions ?? { ar: '', en: '' },
    locationId: method?.locationId ?? '',
  };
}

/** Empty means "not set", which the API stores as null; the API checks the range. */
function timingBody(
  draft: TimingDraft,
): Pick<CreateShippingMethodRequest, 'minBusinessDays' | 'maxBusinessDays' | 'cutoffTime' | 'isPickup' | 'pickupInstructions' | 'locationId'> {
  const days = (value: string) => (value.trim() === '' ? null : Number(value));
  const written = draft.instructions.ar.trim() || draft.instructions.en.trim();
  return {
    minBusinessDays: days(draft.min),
    maxBusinessDays: days(draft.max),
    cutoffTime: draft.cutoff.trim() || null,
    isPickup: draft.isPickup,
    pickupInstructions: draft.isPickup && written ? draft.instructions : null,
    locationId: draft.isPickup && draft.locationId ? draft.locationId : null,
  };
}

/**
 * When it arrives (R6): business days after dispatch, the hour after which an
 * order is tomorrow's, and whether it is collected rather than delivered.
 * Checkout turns these into dates on the store's calendar.
 */
function TimingFields({
  idPrefix,
  draft,
  onChange,
}: {
  idPrefix: string;
  draft: TimingDraft;
  onChange: (next: TimingDraft) => void;
}) {
  const t = useT();
  const set = (patch: Partial<TimingDraft>) => onChange({ ...draft, ...patch });

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={t('deliveryDaysFrom')} htmlFor={`${idPrefix}-min`} hint={t('deliveryDaysHint')}>
          <Input
            id={`${idPrefix}-min`}
            dir="ltr"
            inputMode="numeric"
            value={draft.min}
            onChange={(event) => set({ min: event.target.value.replace(/[^0-9]/g, '') })}
          />
        </Field>
        <Field label={t('deliveryDaysTo')} htmlFor={`${idPrefix}-max`}>
          <Input
            id={`${idPrefix}-max`}
            dir="ltr"
            inputMode="numeric"
            value={draft.max}
            onChange={(event) => set({ max: event.target.value.replace(/[^0-9]/g, '') })}
          />
        </Field>
        <Field label={t('cutoffTime')} htmlFor={`${idPrefix}-cutoff`} hint={t('cutoffTimeHint')}>
          <Input
            id={`${idPrefix}-cutoff`}
            type="time"
            dir="ltr"
            value={draft.cutoff}
            onChange={(event) => set({ cutoff: event.target.value })}
          />
        </Field>
      </div>
      <ToggleField
        label={t('pickupAtStore')}
        hint={t('pickupMethodHint')}
        checked={draft.isPickup}
        onChange={(isPickup) => set({ isPickup })}
      />
      {draft.isPickup ? (
        <>
          <Field label={t('pickupLocation')} htmlFor={`${idPrefix}-location`} hint={t('pickupLocationHint')}>
            <AsyncSelect
              id={`${idPrefix}-location`}
              value={draft.locationId}
              emptyLabel={t('pickupLocationNone')}
              fetchPage={stockLocationOptions.page}
              fetchOne={stockLocationOptions.one}
              onChange={(locationId) => set({ locationId })}
            />
          </Field>
          <BilingualField
            label={t('pickupInstructions')}
            hint={t('pickupInstructionsHint')}
            multiline
            rows={2}
            value={draft.instructions}
            onChange={(instructions) => set({ instructions })}
          />
        </>
      ) : null}
    </div>
  );
}

/**
 * The price table.
 *
 * Amounts are typed in riyals and converted on the digit string (ADR-004). An
 * empty "up to" is the open-ended top band, which is how a free-shipping
 * threshold gets written: 0–300 costs 25, 300 and above costs 0.
 */
function BandEditor({
  bands,
  onChange,
}: {
  bands: DraftBand[];
  onChange: (bands: DraftBand[]) => void;
}) {
  const t = useT();

  const set = (index: number, patch: Partial<DraftBand>) => {
    onChange(bands.map((band, position) => (position === index ? { ...band, ...patch } : band)));
  };

  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-ink">{t('priceBands')}</h3>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-ink-muted">
              <th className="pb-1 text-start font-medium">{t('bandFrom')}</th>
              <th className="pb-1 text-start font-medium">{t('bandTo')}</th>
              <th className="pb-1 text-start font-medium">{t('bandPrice')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {bands.map((band, index) => (
              <tr key={index}>
                <td className="py-1 pe-2">
                  <Input
                    dir="ltr"
                    inputMode="decimal"
                    value={band.from}
                    onChange={(event) => set(index, { from: event.target.value })}
                    className="w-28"
                  />
                </td>
                <td className="py-1 pe-2">
                  <Input
                    dir="ltr"
                    inputMode="decimal"
                    placeholder={t('bandOpenEnded')}
                    value={band.to}
                    onChange={(event) => set(index, { to: event.target.value })}
                    className="w-28"
                  />
                </td>
                <td className="py-1 pe-2">
                  <Input
                    dir="ltr"
                    inputMode="decimal"
                    value={band.price}
                    onChange={(event) => set(index, { price: event.target.value })}
                    className="w-28"
                  />
                </td>
                <td className="py-1">
                  {bands.length > 1 ? (
                    <IconButton
                      label={t('remove')}
                      onClick={() => onChange(bands.filter((_, position) => position !== index))}
                      className="size-9 border-0"
                    >
                      <TrashIcon className="size-4" />
                    </IconButton>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            // A new band starts where the last one ended, because that is the
            // only place it can legally start.
            const last = bands[bands.length - 1];
            onChange([...bands, { from: last?.to || '0.00', to: '', price: '0.00' }]);
          }}
        >
          <PlusIcon className="size-4" />
          {t('addBand')}
        </Button>
        <span className="text-xs text-ink-faint">{t('amountsInRiyals')}</span>
      </div>
    </div>
  );
}

interface DraftBand {
  from: string;
  to: string;
  price: string;
}

function toDraft(band: { minSubtotal: number; maxSubtotal: number | null; price: number }): DraftBand {
  return {
    from: halalasToRiyals(band.minSubtotal),
    to: band.maxSubtotal === null ? '' : halalasToRiyals(band.maxSubtotal),
    price: halalasToRiyals(band.price),
  };
}

/** Null when anything typed is not a usable amount; the API validates the shape. */
function parseBands(bands: DraftBand[]): RateBandInput[] | null {
  const parsed: RateBandInput[] = [];

  for (const band of bands) {
    const min = riyalsToHalalas(band.from);
    const price = riyalsToHalalas(band.price);
    const max = band.to.trim() === '' ? null : riyalsToHalalas(band.to);

    if (min === null || price === null || (band.to.trim() !== '' && max === null)) return null;
    parsed.push({ minSubtotal: min, maxSubtotal: max, price });
  }

  return parsed;
}

/** Weight pricing and what a method will carry (ADR-088), as typed: kilos and riyals. */
interface RulesDraft {
  includedKg: string;
  perKg: string;
  divisor: string;
  maxKg: string;
  excluded: { id: string; label: string }[];
  carrier: string;
}

function toRulesDraft(method?: ShippingMethodAdminDto): RulesDraft {
  return {
    includedKg: method?.includedGrams === null || method?.includedGrams === undefined ? '' : thousandthsToUnits(method.includedGrams) || '0',
    perKg: method?.perKgPrice === null || method?.perKgPrice === undefined ? '' : halalasToRiyals(method.perKgPrice),
    divisor: method?.volumetricDivisor?.toString() ?? '',
    maxKg: method?.maxWeightGrams ? thousandthsToUnits(method.maxWeightGrams) : '',
    excluded: (method?.excludedCategoryIds ?? []).map((id) => ({ id, label: '' })),
    carrier: method?.carrier ?? '',
  };
}

/** What the API is sent — null while a field holds something it could not take. Empty clears. */
function rulesBody(
  draft: RulesDraft,
): Pick<CreateShippingMethodRequest, 'includedGrams' | 'perKgPrice' | 'volumetricDivisor' | 'maxWeightGrams' | 'excludedCategoryIds' | 'carrier'> | null {
  const grams = (value: string, allowZero: boolean): number | null | undefined => {
    const text = value.trim();
    if (!text) return null;
    if (allowZero && /^0+([.,]0*)?$/.test(text)) return 0;
    return unitsToThousandths(text) ?? undefined;
  };
  const includedGrams = grams(draft.includedKg, true);
  const maxWeightGrams = grams(draft.maxKg, false);
  const perKgPrice = draft.perKg.trim() ? (riyalsToHalalas(draft.perKg) ?? undefined) : null;
  const divisor = draft.divisor.trim();
  const volumetricDivisor = divisor ? (/^[1-9]\d{0,5}$/.test(divisor) ? Number(divisor) : undefined) : null;
  if ([includedGrams, maxWeightGrams, perKgPrice, volumetricDivisor].some((value) => value === undefined)) return null;
  return {
    includedGrams: includedGrams as number | null,
    perKgPrice: perKgPrice as number | null,
    volumetricDivisor: volumetricDivisor as number | null,
    maxWeightGrams: maxWeightGrams as number | null,
    excludedCategoryIds: draft.excluded.map((category) => category.id),
    carrier: draft.carrier || null,
  };
}

/**
 * By weight, and what it will carry (ADR-088): kilos the price covers and each
 * kilo above it, the courier's volumetric divisor, a weight limit, categories
 * it will not take, and the courier its parcels go with.
 */
function RulesFields({ idPrefix, draft, onChange }: { idPrefix: string; draft: RulesDraft; onChange: (next: RulesDraft) => void }) {
  const t = useT();
  const { carriers, loadCarriers } = useOrdersStore();
  const set = (patch: Partial<RulesDraft>) => onChange({ ...draft, ...patch });
  const [adding, setAdding] = useState('');

  useEffect(() => {
    void loadCarriers();
  }, [loadCarriers]);

  // Names for categories saved as ids, once.
  const unnamed = draft.excluded.filter((category) => !category.label).map((category) => category.id).join(',');
  useEffect(() => {
    if (!unnamed) return;
    let cancelled = false;
    void Promise.all(unnamed.split(',').map((id) => categoryOptions.one(id))).then((found) => {
      if (cancelled) return;
      const labels = new Map(found.filter((option) => option !== null).map((option) => [option!.id, option!.label]));
      onChange({ ...draft, excluded: draft.excluded.map((category) => ({ ...category, label: category.label || labels.get(category.id) || category.id })) });
    });
    return () => {
      cancelled = true;
    };
    // Only when ids without names appear.
  }, [unnamed]);

  const kg = (field: 'includedKg' | 'maxKg' | 'perKg' | 'divisor', label: string, hint?: string) => (
    <Field label={label} htmlFor={`${idPrefix}-${field}`} hint={hint}>
      <Input
        id={`${idPrefix}-${field}`}
        dir="ltr"
        inputMode={field === 'divisor' ? 'numeric' : 'decimal'}
        value={draft[field]}
        onChange={(event) => set({ [field]: event.target.value } as Partial<RulesDraft>)}
      />
    </Field>
  );

  return (
    <fieldset className="flex flex-col gap-3 rounded-control border border-line p-4">
      <legend className="px-1 text-sm font-medium text-ink">{t('methodRules')}</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        {kg('includedKg', t('methodIncludedKg'), t('methodIncludedKgHint'))}
        {kg('perKg', t('methodPerKg'), t('methodPerKgHint'))}
        {kg('divisor', t('methodVolumetricDivisor'), t('methodVolumetricDivisorHint'))}
        {kg('maxKg', t('methodMaxKg'), t('methodMaxKgHint'))}
      </div>
      <Field label={t('methodCarrier')} htmlFor={`${idPrefix}-carrier`} hint={t('methodCarrierHint')}>
        <Select id={`${idPrefix}-carrier`} className="w-full" value={draft.carrier} onChange={(event) => set({ carrier: event.target.value })}>
          <option value="">{t('methodCarrierNone')}</option>
          {(carriers ?? []).map((carrier) => (
            <option key={carrier.name} value={carrier.name}>
              {carrier.name}
            </option>
          ))}
          {draft.carrier && !(carriers ?? []).some((carrier) => carrier.name === draft.carrier) ? (
            <option value={draft.carrier}>{draft.carrier}</option>
          ) : null}
        </Select>
      </Field>
      <Field label={t('methodExcluded')} htmlFor={`${idPrefix}-excluded`} hint={t('methodExcludedHint')}>
        <AsyncSelect
          id={`${idPrefix}-excluded`}
          value={adding}
          emptyLabel={t('methodExcludedAdd')}
          fetchPage={(search, cursor) => categoryOptions.page(search, cursor)}
          fetchOne={(id) => categoryOptions.one(id)}
          onChange={(id, option) => {
            setAdding('');
            if (id && !draft.excluded.some((category) => category.id === id)) {
              set({ excluded: [...draft.excluded, { id, label: option?.label ?? id }] });
            }
          }}
        />
      </Field>
      {draft.excluded.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {draft.excluded.map((category) => (
            <button
              key={category.id}
              type="button"
              className="rounded-full border border-line px-2.5 py-0.5 text-xs text-ink hover:border-danger hover:text-danger"
              onClick={() => set({ excluded: draft.excluded.filter((entry) => entry.id !== category.id) })}
              aria-label={t('remove')}
            >
              {category.label || '…'} ×
            </button>
          ))}
        </div>
      ) : null}
      {rulesBody(draft) === null ? <p className="text-xs text-danger">{t('methodRulesInvalid')}</p> : null}
    </fieldset>
  );
}
